import {
  ArrowDownward,
  ArrowUpward,
  LinearScaleOutlined,
  TableChartOutlined,
} from "@mui/icons-material";
import { alpha, Card, Stack, styled, Typography } from "@mui/material";
import type { ReactNode } from "react";

import { Chip } from "../chip/Chip";
import { ANALYTIC_TABLE_TITLE } from "../table-insight/constants";
import {
  type TableInsight,
  TableInsightAction,
} from "../table-insight/TableInsightAction";
import { getTendencyInfo } from "./utils";

export const TwoColumnLayout = styled(Stack)({
  display: "grid",
  gridTemplateColumns: "2fr 1fr",
  gap: 16,
});

export const SectionCard = styled(Card)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.common.white, 0.5),
  borderRadius: 12,
  padding: "16px 16px 0 16px",
  border: `1.5px solid ${theme.palette.common.white}`,
}));

export interface ScrollableTableProps {
  children: ReactNode;
  maxRows?: number;
}

export type FlatTwoColumnLayoutProps = {
  period: string;
  title: string;
  tableDescription: string;
  subtitle: string;
  tendency?: number;
  currency: string;
  units: string;
  /** Mara insights of the table. The Ask Mara icon is shown only when provided. */
  tableInsight?: TableInsight;
  children: {
    chart: ReactNode;
    table: ReactNode;
  };
};

export const DEFAULT_MAX_ROWS = 7.5;
export const TABLE_ROW_HEIGHT = 40;

export function ScrollableTable({
  children,
  maxRows = DEFAULT_MAX_ROWS,
}: ScrollableTableProps) {
  return (
    <Stack
      sx={{
        mt: 3,
        padding: 0,
        maxHeight: `calc(${maxRows} * ${TABLE_ROW_HEIGHT}px)`,
        overflow: "auto",
      }}
    >
      {children}
    </Stack>
  );
}

export function FlatTwoColumnLayout({
  tableDescription,
  tendency,
  tableInsight,
  children,
}: FlatTwoColumnLayoutProps) {
  const tendencyInfo = getTendencyInfo(tendency ?? 0);
  const {
    color: tendencyColor,
    label: tendencyLabel,
    type: tendencyType,
  } = tendencyInfo;

  // Get the tendency icon based on the type
  const getTendencyIcon = () => {
    switch (tendencyType) {
      case "increase":
        return <ArrowUpward />;
      case "neutral":
        return <LinearScaleOutlined />;
      case "decrease":
        return <ArrowDownward />;
    }
  };

  return (
    <Stack gap={2}>
      <TwoColumnLayout>
        {/* Left Card - Chart */}
        <SectionCard>{children.chart}</SectionCard>

        {/* Right Card - Table */}
        <SectionCard>
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
          >
            <Stack gap={1}>
              <Stack direction={"row"} gap={1}>
                <TableChartOutlined
                  sx={(theme) => ({ color: theme.palette.primary.light })}
                />
                <Typography
                  variant="h5"
                  sx={(theme) => ({ color: theme.palette.primary.dark })}
                >
                  {ANALYTIC_TABLE_TITLE}
                </Typography>
              </Stack>
              <Typography variant="overline">{tableDescription}</Typography>
            </Stack>
            <Stack direction="row" gap={1} alignItems="center">
              {tendency !== null && tendency !== undefined && (
                <Chip
                  label={tendencyLabel}
                  icon={getTendencyIcon()}
                  size="small"
                  color={tendencyColor || undefined}
                />
              )}
              {tableInsight && (
                <TableInsightAction
                  title={ANALYTIC_TABLE_TITLE}
                  {...tableInsight}
                />
              )}
            </Stack>
          </Stack>

          <ScrollableTable>{children.table}</ScrollableTable>
        </SectionCard>
      </TwoColumnLayout>
    </Stack>
  );
}
